import { useMutation, useQuery } from '@tanstack/react-query'
import type { Message } from '@twilio/conversations'
import api from 'lib/interceptor/axios.interceptor'
import type { TwilioConversationData } from 'models/chat'

const chatCompletion = async (message: {
	system: string
	user: string[] | null
}) => {
	const response = await api.post(
		`/ai/api/chat/completions`,
		{ messages: message },
		{ responseType: 'stream' }
	)
	return JSON.parse(response.data as string) as string[] | null
}

export const getConversation = async (uniqueName: string) => {
	const response = await api.get(`/msg/api/conversations/${uniqueName}`)
	return response.data as TwilioConversationData
}

export const getArchiveConversation = async (uniqueName: string) => {
	const response = await api.get(`/msg/api/conversations/${uniqueName}/archive`)
	return response.data as Message[]
}

const getChatToken = async (userId: string) => {
	const response = await api.post(`/msg/api/chat/token`, { userId })
	return response.data as string
}

const joinConversation = async (conversationId: string, userId: string) => {
	const response = await api.post(
		`/msg/api/conversations/${conversationId}/participants`,
		[userId]
	)
	return response.data as unknown
}

export const useChatCompletions = () =>
	useMutation({
		mutationKey: ['delete-hotelier'],
		mutationFn: async ({
			message
		}: {
			message: { system: string; user: string[] | null }
		}) => chatCompletion(message)
	})

export const useGetChatToken = (userId: string, enabled = true) =>
	useQuery({
		queryKey: ['chat-token', userId],
		queryFn: async () => getChatToken(userId),
		enabled
	})

export const useGetConversation = (uniqueName: string, enabled = true) =>
	useQuery({
		queryKey: ['conversation', uniqueName],
		queryFn: async () => getConversation(uniqueName),
		enabled
	})

export const useGetArchiveConversation = (uniqueName: string, enabled = true) =>
	useQuery({
		queryKey: ['archive-conversation', uniqueName],
		queryFn: async () => getArchiveConversation(uniqueName),
		enabled
	})

export const useJoinConversation = () =>
	useMutation({
		mutationKey: ['join-conversation'],
		mutationFn: async ({
			conversationId,
			userId
		}: {
			conversationId: string
			userId: string
		}) => joinConversation(conversationId, userId)
	})
